import React from 'react';
import { observer } from 'mobx-react';
import { MatField } from 'src/core/components/mat-field';
import { FieldState } from 'formstate';
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    FormHelperText,
} from '@material-ui/core';
import * as styles from './application.less';
import { PersonalInfoForm, DocumentForm } from '../../forms/application-information-forms';
import * as Utils from '../../forms/application-information-forms-utils';

interface IApplicantProps {
    personalInfo: PersonalInfoForm;
    document: DocumentForm;
    states: string[];
    validate: (fieldState: FieldState<string>) => () => Promise<void>;
}

enum idType {
    driversLicense = 'Driver License',
    stateId = 'State ID',
    passport = 'Passport',
    militaryId = 'Military ID',
    usaGreenCard = 'USA Green Card',
}

export const Applicant: React.FC<IApplicantProps> = observer(
    ({ personalInfo, document, validate, states }) => {
        const idTypes = [idType.driversLicense, idType.stateId, idType.passport, idType.militaryId, idType.usaGreenCard];
        return (
            <React.Fragment>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.FirstName}
                        maxLength={50}
                        label="First Name"
                        onBlur={validate(personalInfo.$.FirstName)}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.MiddleName}
                        maxLength={1}
                        label="Middle Initial (Optional - first letter only)"
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.LastName}
                        maxLength={50}
                        label="Last Name"
                        onBlur={validate(personalInfo.$.LastName)}
                    />
                </div>
                <div className={styles.row}>
                    <TextField
                        required
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        value={personalInfo.$.DateOfBirth.value}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        helperText={
                            personalInfo.$.DateOfBirth.dirty! && personalInfo.$.DateOfBirth.error
                        }
                        error={
                            personalInfo.$.DateOfBirth.dirty! && personalInfo.$.DateOfBirth.hasError
                        }
                        onChange={async (e) => {
                            personalInfo.$.DateOfBirth.onChange(e.currentTarget.value);
                            await validate(personalInfo.$.DateOfBirth)();
                        }}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.DisplaySsn}
                        maxLength={11}
                        label="Social Security Number"
                        formatter={Utils.formatSsn}
                        onBlur={async (e) => {
                            personalInfo.$.Ssn.onChange(personalInfo.$.DisplaySsn.value);
                            personalInfo.$.DisplaySsn.onChange(
                                Utils.hideSsn(personalInfo.$.DisplaySsn.value)
                            );
                            await validate(personalInfo.$.DisplaySsn)();
                        }}
                        onFocus={(e) => {
                            personalInfo.$.DisplaySsn.onChange(personalInfo.$.Ssn.value);
                        }}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.Email}
                        maxLength={50}
                        label="Email"
                        onBlur={validate(personalInfo.$.Email)}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.PhoneNumber}
                        maxLength={14}
                        label="Phone Number"
                        formatter={Utils.formatPhoneNumber}
                        onBlur={validate(personalInfo.$.PhoneNumber)}
                    />
                </div>
                <div className={styles.row}>
                    <FormControl
                        required
                        fullWidth
                        error={document.$.Type.dirty! && document.$.Type.hasError}
                    >
                        <InputLabel htmlFor="select">Id Type</InputLabel>
                        <Select
                            inputProps={{ id: 'typeid' }}
                            value={document.$.Type.value}
                            onChange={async (e) => {
                                document.$.Type.onChange(e.target.value as string);
                                await validate(document.$.Type)();
                            }}
                        >
                            {' '}
                            {idTypes.map((value, index) => (
                                <MenuItem key={index} value={value as string}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {document.$.Type.dirty! && document.$.Type.error}
                        </FormHelperText>
                    </FormControl>
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={document.$.Number}
                        maxLength={15}
                        label="Document Number"
                        onBlur={validate(document.$.Number)}
                    />
                </div>
                {![idType.militaryId.toString(), idType.passport.toString(), idType.usaGreenCard.toString()].includes(
                    document.$.Type.value
                ) ? (
                    <div className={styles.row}>
                        <FormControl required>
                            <InputLabel htmlFor="select">State</InputLabel>
                            <Select
                                inputProps={{ id: 'applicantState' }}
                                value={document.$.State.value}
                                onChange={(e) => {
                                    document.$.State.onChange(e.target.value as string);
                                }}
                            >
                                {states.map((value, index) => (
                                    <MenuItem key={index} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                ) : null}
                {(document.$.Type.value === idType.passport || document.$.Type.value === idType.usaGreenCard) ? (
                    <div className={styles.row}>
                        <MatField
                            required
                            fullWidth
                            errorColor
                            fieldState={document.$.State}
                            maxLength={15}
                            label="Country"
                            onBlur={validate(document.$.State)}
                        />
                    </div>
                ) : null}
                {(document.$.Type.value !== idType.usaGreenCard) ? (
                    <div className={styles.row}>
                        <TextField
                            required
                            fullWidth
                            label="Issue Date"
                            type="date"
                            value={document.$.IssueDate.value}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText={document.$.IssueDate.dirty! && document.$.IssueDate.error}
                            error={document.$.IssueDate.dirty! && document.$.IssueDate.hasError}
                            onChange={async (e) => {
                                document.$.IssueDate.onChange(e.currentTarget.value);
                                await validate(document.$.IssueDate)();
                            }}
                        />
                    </div>
                ) : null}
                <div className={styles.row}>
                    <TextField
                        required
                        fullWidth
                        label="Expiration Date"
                        type="date"
                        value={document.$.ExpirationDate.value}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        helperText={
                            document.$.ExpirationDate.dirty! && document.$.ExpirationDate.error
                        }
                        error={
                            document.$.ExpirationDate.dirty! && document.$.ExpirationDate.hasError
                        }
                        onChange={async (e) => {
                            document.$.ExpirationDate.onChange(e.currentTarget.value);
                            await validate(document.$.ExpirationDate)();
                        }}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.EmployerName}
                        maxLength={50}
                        label="Employer Name"
                        onBlur={validate(personalInfo.$.EmployerName)}
                    />
                </div>
                <div className={styles.row}>
                    <MatField
                        required
                        fullWidth
                        errorColor
                        fieldState={personalInfo.$.GrossMonthlyIncome}
                        formatter={Utils.formatAmount}
                        validator={Utils.amountValidator}
                        maxLength={50}
                        startAdornment="$"
                        label="Gross Monthly Income ($)"
                        onBlur={validate(personalInfo.$.GrossMonthlyIncome)}
                    />
                </div>
            </React.Fragment>
        );
    }
);
